export type TreatmentPlanStatus = 'DRAFT' | 'ACTIVE' | 'ON_HOLD' | 'COMPLETED' | 'DISCONTINUED';

export type TreatmentPlanPriority = 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';

export type TreatmentPlanGoal = {
  id?: string;
  title: string;
  description?: string;
  metric?: string;
  targetValue?: string;
  unit?: string;
  timeframeDays?: number;
};

export type TreatmentPlanIntervention = {
  id?: string;
  type?: string;
  summary: string;
  frequency?: string;
  durationDays?: number;
  ownerRole?: string;
  instructions?: string;
};

export type TreatmentPlanMedication = {
  id?: string;
  medication: string;
  dose?: string;
  route?: string;
  frequency?: string;
  durationDays?: number;
  indication?: string;
  instructions?: string;
  prn?: boolean;
};

export type TreatmentPlanMonitoring = {
  id?: string;
  metric: string;
  targetRange?: string;
  frequency?: string;
  alertThreshold?: string;
  actionIfOutOfRange?: string;
};

export type TreatmentPlanFollowUp = {
  id?: string;
  modality: string;
  intervalDays?: number;
  ownerRole?: string;
  purpose?: string;
};

export type TreatmentPlanEducation = {
  id?: string;
  topic: string;
  objective?: string;
  materials?: string;
  required?: boolean;
};

export type TreatmentPlanEscalationRule = {
  id?: string;
  trigger: string;
  action: string;
  severity?: 'low' | 'medium' | 'high';
};

export type TreatmentPlanCareTeamMember = {
  id?: string;
  role: string;
  memberName?: string;
  responsibility?: string;
  contact?: string;
};

export type TreatmentPlanTemplate = {
  code: string;
  name: string;
  description?: string;
  category?: string;
  tags: string[];
  version: number;
  active: boolean;
  estimatedDurationDays?: number;
  reviewCadenceDays?: number;
  defaultPatientInstructions?: string;
  defaultClinicianNotes?: string;
  goals: TreatmentPlanGoal[];
  interventions: TreatmentPlanIntervention[];
  medications: TreatmentPlanMedication[];
  monitoring: TreatmentPlanMonitoring[];
  followUps: TreatmentPlanFollowUp[];
  education: TreatmentPlanEducation[];
  escalationRules: TreatmentPlanEscalationRule[];
  careTeam: TreatmentPlanCareTeamMember[];
};

export type TreatmentPlanVisitProgress = {
  linkedAppointmentCount: number;
  attendedVisitCount: number;
  completedVisitCount: number;
  upcomingVisitCount: number;
  remainingSuggestedVisitCount: number | null;
  firstAttendedVisitAt: string | null;
  lastAttendedVisitAt: string | null;
  nextScheduledVisitAt: string | null;
  lastCompletedVisitAt: string | null;
};

export type TreatmentPlanLinkedNote = {
  id: string;
  title: string;
  updatedAt: string;
  authorName: string | null;
};

export type TreatmentPlanConfigResponse = {
  templates: TreatmentPlanTemplate[];
};

export type PatientTreatmentPlan = {
  id: string;
  organizationId: string;
  patientId: string;
  templateCode: string | null;
  templateName: string | null;
  templateVersion: number | null;
  title: string;
  diagnosis: string | null;
  diagnosisCodes: string[];
  category: string | null;
  priority: TreatmentPlanPriority | null;
  status: TreatmentPlanStatus;
  suggestedVisitCount: number | null;
  visitProgress: TreatmentPlanVisitProgress;
  linkedNotes: TreatmentPlanLinkedNote[];
  startDate: string | null;
  targetEndDate: string | null;
  completedAt: string | null;
  discontinuedAt: string | null;
  reviewCadenceDays: number | null;
  nextReviewAt: string | null;
  lastReviewedAt: string | null;
  goals: TreatmentPlanGoal[];
  interventions: TreatmentPlanIntervention[];
  medications: TreatmentPlanMedication[];
  monitoring: TreatmentPlanMonitoring[];
  followUps: TreatmentPlanFollowUp[];
  education: TreatmentPlanEducation[];
  escalationRules: TreatmentPlanEscalationRule[];
  careTeam: TreatmentPlanCareTeamMember[];
  patientInstructions: string | null;
  clinicianNotes: string | null;
  barriers: string[];
  outcomes: string[];
  progressPercent: number;
  adherencePercent: number | null;
  metadata: Record<string, unknown>;
  createdById: string | null;
  updatedById: string | null;
  createdAt: string;
  updatedAt: string;
};

export type PatientTreatmentPlanListResponse = {
  data: PatientTreatmentPlan[];
};

export function formatTreatmentPlanDate(value?: string | null) {
  if (!value) return '-';
  return new Date(value).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });
}
